import { registerLocaleData } from '@angular/common';
import { provideHttpClient, withInterceptors, withXhr } from '@angular/common/http';
import localeDe from '@angular/common/locales/de';
import localeEn from '@angular/common/locales/en';
import localeEs from '@angular/common/locales/es';
import localePt from '@angular/common/locales/pt';
import {
  ApplicationConfig,
  ErrorHandler,
  LOCALE_ID,
  inject,
  isDevMode,
  provideBrowserGlobalErrorListeners,
  provideZonelessChangeDetection,
} from '@angular/core';
import { provideSignalFormsConfig } from '@angular/forms/signals';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import {
  provideRouter,
  withComponentInputBinding,
  withRouterConfig,
  withViewTransitions,
} from '@angular/router';

import { AppErrorHandler } from '@nbx/core/http';
import { LANGUAGES, provideLanguage, providePrimeNgLocale } from '@nbx/core/language';
import { SESSION_KEY } from '@nbx/core/state';
import { provideNbxTheme } from '@nbx/core/theme';
import { PRIMENG_LICENSE, createNbxPreset } from '@nbx/styles/prime-ng';

import { MessageService } from 'primeng/api';
import { providePrimeNG } from 'primeng/config';

import { TranslocoHttpLoader } from '@core/services/transloco-loader';
import { primeNgDateNamesFor } from '@core/utils/primeng-date-names.util';
import { HTTP_INTERCEPTORS } from '@interceptors/interceptors';
import { TranslocoService, provideTransloco } from '@jsverse/transloco';
import { provideTranslocoMessageformat } from '@jsverse/transloco-messageformat';
import { AuthStore } from '@store/auth.store';

import { routes } from './app.routes';

registerLocaleData(localeEs, 'es');
registerLocaleData(localeEn, 'en');
registerLocaleData(localeDe, 'de');
registerLocaleData(localePt, 'pt');

const AVAILABLE_LANGUAGES = ['es', 'en', 'de', 'pt'];

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    { provide: ErrorHandler, useClass: AppErrorHandler },
    provideZonelessChangeDetection(),
    provideAnimationsAsync(),
    provideSignalFormsConfig({
      classes: {
        // eslint-disable-next-line @typescript-eslint/naming-convention -- CSS class name, applied to [formField] hosts (native PrimeNG inputs) once the field is invalid and touched.
        'p-invalid': field => field.state().invalid() && field.state().touched(),
      },
    }),
    provideRouter(
      routes,
      withRouterConfig({ paramsInheritanceStrategy: 'always' }),
      withComponentInputBinding(),
      withViewTransitions()
    ),
    provideHttpClient(withXhr(), withInterceptors(HTTP_INTERCEPTORS)),
    provideLanguage(),
    { provide: SESSION_KEY, useFactory: () => inject(AuthStore).session },
    { provide: LANGUAGES, useValue: AVAILABLE_LANGUAGES },
    { provide: LOCALE_ID, useFactory: () => inject(TranslocoService).getActiveLang() },
    MessageService,
    // The back office draws its calendars Monday-first with Intl's date names.
    providePrimeNgLocale({ dateNames: primeNgDateNamesFor }),
    provideNbxTheme({ mode: 'rentals', darkClass: 'dark-mode' }),
    providePrimeNG({
      license: PRIMENG_LICENSE,
      theme: {
        preset: createNbxPreset(),
        options: {
          darkModeSelector: '.dark-mode',
          cssLayer: {
            name: 'primeng',
            order: 'theme, base, primeng, typography, components, utilities',
          },
        },
      },
      zIndex: {
        modal: 1100,
        overlay: 1300,
        menu: 1300,
        tooltip: 1400,
      },
    }),
    provideTransloco({
      config: {
        availableLangs: AVAILABLE_LANGUAGES,
        defaultLang: 'es',
        fallbackLang: 'es',
        reRenderOnLangChange: true,
        prodMode: !isDevMode(),
        missingHandler: {
          useFallbackTranslation: true,
          logMissingKey: isDevMode(),
        },
      },
      loader: TranslocoHttpLoader,
    }),
    provideTranslocoMessageformat(),
  ],
};
